import React, { useState } from "react";
import { ArrowLeft, Landmark, Lock, Loader2, Check, CheckCircle2, Circle, ScrollText, Send, Building2, BadgeCheck } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";

const ORDER = ["pending", "sent", "received", "released"];

function Field({ label, value, mono, strong }) {
  return (
    <div>
      <dt className="tech-label text-muted-foreground">{label}</dt>
      <dd className={`mt-1 text-foreground ${mono ? "font-mono text-sm" : "text-sm"} ${strong ? "font-serif text-lg font-semibold" : ""}`}>{value}</dd>
    </div>
  );
}

function HorizontalStepper({ currentIndex }) {
  const { t } = useLanguage();
  const steps = [
    { label: t("client.stepper.order"), Icon: ScrollText, tone: "gold" },
    { label: t("client.step.reported"), Icon: Send, tone: "amber" },
    { label: t("client.step.received"), Icon: Building2, tone: "emerald" },
    { label: t("client.step.released"), Icon: BadgeCheck, tone: "emerald" },
  ];
  const activeTone = {
    gold: { ring: "neon-ring-gold", node: "border-accent bg-accent/20 text-accent", icon: "text-accent" },
    amber: { ring: "neon-ring-amber", node: "border-amber-400 bg-amber-400/20 text-amber-500", icon: "text-amber-500" },
    emerald: { ring: "neon-ring-emerald", node: "border-emerald-400 bg-emerald-400/20 text-emerald-500", icon: "text-emerald-500" },
  };
  return (
    <ol className="flex items-start">
      {steps.map((s, i) => {
        const done = currentIndex > i;
        const active = currentIndex === i;
        const reached = done || active;
        const tone = activeTone[s.tone];
        return (
          <li key={s.label} className="flex flex-1 flex-col items-center">
            <div className="flex w-full items-center">
              <span className={`h-px flex-1 ${i === 0 ? "opacity-0" : currentIndex >= i ? "bg-accent" : "bg-border"}`} />
              <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border transition-colors ${active ? `${tone.node} ${tone.ring}` : done ? "border-accent/60 bg-accent/15 text-accent gold-ring" : "border-border bg-secondary text-muted-foreground"}`}>
                {done ? <Check className="h-5 w-5" /> : <s.Icon className={`h-5 w-5 ${active ? tone.icon : ""}`} />}
              </span>
              <span className={`h-px flex-1 ${i === steps.length - 1 ? "opacity-0" : currentIndex > i ? "bg-accent" : "bg-border"}`} />
            </div>
            <span className={`mt-2.5 px-1 text-center text-xs font-medium ${active ? tone.icon : reached ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

function TimelineItem({ done, active, title, desc, last, accent = "emerald" }) {
  const dotColor = done ? (accent === "amber" ? "bg-amber-500 text-white" : "bg-emerald-600 text-white") : active ? "bg-amber-500 text-white" : "bg-muted text-muted-foreground";
  return (
    <li className="flex gap-4">
      <div className="flex flex-col items-center">
        <span className={`flex h-7 w-7 items-center justify-center rounded-full ${dotColor}`}>{done ? <Check className="h-4 w-4" /> : <Circle className="h-3 w-3" />}</span>
        {!last && <span className={`w-px flex-1 ${done ? "bg-emerald-300" : "bg-border"}`} />}
      </div>
      <div className="pb-6">
        <p className={`text-sm font-medium ${done || active ? "text-foreground" : "text-muted-foreground"}`}>{title}</p>
        {desc && <p className="mt-0.5 text-xs text-muted-foreground">{desc}</p>}
      </div>
    </li>
  );
}

export function ClientPaymentProcess({ initialTx, onReset }) {
  const { t, lang } = useLanguage();
  const [tx, setTx] = useState(initialTx);
  const [busy, setBusy] = useState(false);

  const fmtMoney = (a, c) => new Intl.NumberFormat(lang === "de" ? "de-DE" : "en-US", { style: "currency", currency: c }).format(Number(a));
  const formatIban = (iban) => (iban ? iban.replace(/(.{4})/g, "$1 ").trim() : "—");
  const productLabel = (v) => (v === "festgeld" ? t("admin.product.festgeld") : v === "tagesgeld" ? t("admin.product.tagesgeld") : null);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/me/transactions/${tx.id}/confirm-sent`);
      if (data) setTx(data);
    } finally { setBusy(false); }
  };

  const currentIndex = ORDER.indexOf(tx.status);
  const awaitingPayment = tx.status === "pending";
  const steps = [
    { key: "sent", title: t("client.step.reported"), desc: t("client.step.reportedDesc") },
    { key: "received", title: t("client.step.received"), desc: t("client.step.receivedDesc") },
    { key: "released", title: t("client.step.released"), desc: t("client.step.releasedDesc") },
  ];

  return (
    <div>
      <button type="button" onClick={onReset} className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground">
        <ArrowLeft className="h-4 w-4" />{t("client.ref.reset")}
      </button>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl font-semibold tracking-tight text-foreground">{t("client.pay.title")}</h1>
          <p className="mt-1 font-mono text-sm text-muted-foreground">{tx.reference}</p>
        </div>
        <StatusBadge status={tx.status} />
      </div>

      <Card className="premium-card mt-6 p-6"><HorizontalStepper currentIndex={currentIndex} /></Card>

      <Card className="premium-card mt-6 overflow-hidden p-0">
        <div className="flex items-center justify-between border-b border-border bg-sidebar px-6 py-4 text-sidebar-foreground">
          <div className="flex items-center gap-2.5"><Landmark className="h-5 w-5 text-sidebar-primary" /><span className="font-medium">{tx.targetBank}</span></div>
          <span className="inline-flex items-center gap-1.5 text-xs text-sidebar-foreground/70"><Lock className="h-3.5 w-3.5" />{t("client.identity.verified")}</span>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm leading-relaxed text-muted-foreground">{t("client.pay.instruction")}</p>
          <dl className="mt-6 grid gap-x-8 gap-y-5 sm:grid-cols-2">
            {productLabel(tx.productType) && <Field label={t("client.pay.product")} value={productLabel(tx.productType)} />}
            <Field label={t("client.pay.recipient")} value={tx.targetRecipient ?? tx.investorName} />
            <Field label={t("client.pay.bank")} value={tx.targetBank} />
            <Field label={t("client.pay.iban")} value={formatIban(tx.targetIban)} mono />
            <Field label={t("client.pay.bic")} value={tx.targetBic ?? "—"} mono />
            <Field label={t("client.pay.purpose")} value={tx.purposeText || tx.purposeCode} mono />
            {tx.settlementBank && <Field label={t("client.pay.settlement")} value={tx.settlementBank} />}
            <Field label={t("client.pay.amount")} value={fmtMoney(tx.amount, tx.currency)} strong />
          </dl>
          {tx.settlementBank && (
            <p className="mt-4 rounded-md border border-border bg-secondary/40 px-4 py-3 text-xs leading-relaxed text-muted-foreground">{t("client.pay.settlement.note")}</p>
          )}
          {awaitingPayment ? (
            <div className="mt-6 border-t border-border pt-5">
              <Button onClick={confirm} disabled={busy} size="lg" className="w-full sm:w-auto">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" />{t("client.pay.confirming")}</> : <><Check className="h-4 w-4" />{t("client.pay.confirm")}</>}
              </Button>
              <p className="mt-3 text-xs text-muted-foreground">{t("client.pay.confirmHint")}</p>
            </div>
          ) : (
            <div className="mt-6 flex items-center gap-2 rounded-md border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-sm font-medium text-emerald-600">
              <CheckCircle2 className="h-4 w-4" />{t("client.pay.done")}
            </div>
          )}
        </div>
      </Card>

      <Card className="premium-card mt-6 p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-medium text-foreground">{t("client.process.title")}</h2>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2.5 py-0.5 text-xs font-medium text-emerald-600">
            <span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" /></span>
            {t("portal.live")}
          </span>
        </div>
        <ol className="mt-5 space-y-0">
          <TimelineItem done={currentIndex >= 1} active={awaitingPayment} title={t("client.step.awaiting")} desc="" />
          {steps.map((s, i) => {
            const stepIndex = ORDER.indexOf(s.key);
            return <TimelineItem key={s.key} done={currentIndex >= stepIndex} active={currentIndex === stepIndex} title={s.title} desc={s.desc} last={i === steps.length - 1} accent={s.key === "sent" ? "amber" : "emerald"} />;
          })}
        </ol>
      </Card>
    </div>
  );
}
